import clsx from "clsx";
import { motion } from "framer-motion";
import { useLocale } from "@/i18n";
import { DURATION, EASE_OUT_EXPO, usePrefersReducedMotion } from "@/lib/motion";

export type BookingStepDef = {
  id: number;
  label: string;
};

type Props = {
  steps: BookingStepDef[];
  current: number;
};

export function BookingStepIndicator({ steps, current }: Props) {
  const { messages: m } = useLocale();
  const reducedMotion = usePrefersReducedMotion();

  return (
    <nav aria-label={m.flow.pageTitle} className="mb-5">
      <ol className="flex items-start justify-between gap-0.5 rounded-2xl border border-slate-200/80 bg-white/70 px-2 py-3 md:gap-2 md:px-4 lg:gap-3">
        {steps.map((s, index) => {
          const done = s.id < current;
          const active = s.id === current;
          return (
            <li
              key={s.id}
              className="flex min-w-0 flex-1 flex-col items-center"
              aria-current={active ? "step" : undefined}
            >
              <div className="flex w-full items-center">
                {index > 0 ? (
                  <span
                    className={clsx(
                      "bk-step-connector",
                      done || active ? "bg-brand-500" : "bg-slate-200",
                    )}
                    style={
                      reducedMotion
                        ? undefined
                        : {
                            transform: done || active ? "scaleX(1)" : "scaleX(1)",
                            opacity: done || active ? 1 : 0.5,
                          }
                    }
                    aria-hidden
                  />
                ) : (
                  <span className="min-w-2 flex-1" aria-hidden />
                )}
                <motion.span
                  className={clsx(
                    "bk-step-dot flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold",
                    done && "bg-brand-600 text-white",
                    active && !done && "bg-brand-600 text-white ring-4 ring-brand-500/20",
                    !done && !active && "border border-slate-200 bg-white text-slate-400",
                  )}
                  animate={
                    reducedMotion
                      ? undefined
                      : {
                          scale: active ? 1.08 : 1,
                        }
                  }
                  transition={{ duration: DURATION.normal, ease: EASE_OUT_EXPO }}
                >
                  {done ? (
                    <motion.svg
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden
                      initial={reducedMotion ? false : { scale: 0, rotate: -45 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 400, damping: 20 }}
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </motion.svg>
                  ) : (
                    s.id
                  )}
                </motion.span>
                {index < steps.length - 1 ? (
                  <span
                    className={clsx(
                      "bk-step-connector",
                      done ? "bg-brand-500" : "bg-slate-200",
                    )}
                    style={
                      reducedMotion
                        ? undefined
                        : {
                            opacity: done ? 1 : 0.5,
                          }
                    }
                    aria-hidden
                  />
                ) : (
                  <span className="min-w-2 flex-1" aria-hidden />
                )}
              </div>
              <motion.span
                className={clsx(
                  "mt-1.5 max-w-full truncate px-0.5 text-center text-[0.625rem] font-medium leading-tight sm:text-[0.6875rem] md:overflow-visible md:whitespace-normal md:text-xs lg:text-sm",
                  active ? "text-brand-700" : done ? "text-slate-700" : "text-slate-400",
                )}
                animate={
                  reducedMotion
                    ? undefined
                    : {
                        opacity: active ? 1 : done ? 0.85 : 0.6,
                        fontWeight: active ? 600 : 500,
                      }
                }
                transition={{ duration: DURATION.fast }}
              >
                {s.label}
              </motion.span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
